import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, catchError, firstValueFrom, map, of } from "rxjs";
import { AccessTokenResponse, ApiException, IAccessTokenResponse, IdentityClient, LoginRequest, ManageClient, RefreshRequest } from "../api_identity/api";

export interface IIdentityAuthService {
  getAccessToken(): Promise<string | undefined>;
  getRefreshToken(): Promise<string | undefined>;
  getToken(): Promise<IAccessTokenResponse | undefined>;
  setToken(token: IAccessTokenResponse): Promise<void>;
}

export enum AuthState {
  Init,
  LoggedIn,
  LoggedOut
}

@Injectable({
  providedIn: 'root',
})
export class IdentityAuthService implements IIdentityAuthService {

  constructor(private client: IdentityClient, private manageClient: ManageClient) {
    this.loadToken();
  }

  private _authState: AuthState = AuthState.Init;
  private _authStateChanged: Subject<AuthState> = new BehaviorSubject<AuthState>(this._authState);

  private _tokenExpires: Date = new Date();
  private _token?: AccessTokenResponse;

  async getAccessToken() {
    let expires = new Date();
    expires.setMinutes(expires.getMinutes() + 1);

    if (!this._token || this._tokenExpires < expires) {
      await firstValueFrom(this.refreshToken());
    }

    return this._token?.AccessToken;
  }

  getRefreshToken() {
    return Promise.resolve(this._token?.RefreshToken);
  }

  setRefreshToken(token: string) {
    if (!this._token) {
      this._token = new AccessTokenResponse();
    }

    this._token.RefreshToken = token;
  }

  getToken() {
    return Promise.resolve(this._token);
  }

  setToken(token: IAccessTokenResponse) {
    this._authState = AuthState.Init;
    this._token = new AccessTokenResponse(token);
    this._tokenExpires = new Date();
    this._tokenExpires.setSeconds(this._tokenExpires.getSeconds() + (token.ExpiresIn ?? 0));
    this.saveToken();
    this.setAuthState(AuthState.LoggedIn);
    return Promise.resolve();
  }

  loadToken() {
    try {
      let tokenString = localStorage.getItem('token');
      let tokenExpiresString = localStorage.getItem('tokenExpires');
      if (tokenString && tokenExpiresString) {
        this._token = AccessTokenResponse.fromJS(JSON.parse(tokenString));
        this._tokenExpires = new Date(tokenExpiresString);
        return { token: this._token, tokenExpires: this._tokenExpires };
      }
    } catch (ex) {
      console.debug(ex);
      this.clearToken();
    }
    return null;
  }

  saveToken() {
    localStorage.setItem('token', JSON.stringify(this._token?.toJSON()));
    localStorage.setItem('tokenExpires', this._tokenExpires.toString());
  }

  clearToken() {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpires');
    localStorage.removeItem('user');
    this._token = undefined;
  }

  setAuthState(state: AuthState, token?: AccessTokenResponse) {
    if (token) {
      this._token = token;
      this._tokenExpires = new Date();
      this._tokenExpires.setSeconds(this._tokenExpires.getSeconds() + token.ExpiresIn);
      this.saveToken();
    }

    if (this._authState !== state) {
      this._authState = state;
      this._authStateChanged.next(state);
    }
  }

  public onStateChanged() {
    return this._authStateChanged.asObservable();
  }

  public signIn(email: string, password: string) {
    return this.client.login(new LoginRequest({
      Email: email,
      Password: password
    })).pipe(map(response => {
      this.setAuthState(AuthState.LoggedIn, response);
      return response;
    }
    ));
  }

  public refreshToken() {
    if (this._token?.RefreshToken) {
      return this.client.refresh(new RefreshRequest({ RefreshToken: this._token?.RefreshToken })).pipe(map(response => {
        this.setAuthState(AuthState.LoggedIn, response);
        return response;
      }),
        catchError(error => {
          console.log(error);
          if (error instanceof ApiException && error.status === 401) {
            this.clearToken();
            this.setAuthState(AuthState.LoggedOut);
          }
          throw error;
        }));
    } else {
      return of(null);
    }
  }

  private _currentSignOutPromise?: Promise<boolean> = undefined;

  // sign out
  public signOut() {
    if (this._currentSignOutPromise) {
      return this._currentSignOutPromise;
    }

    this._currentSignOutPromise = (async () => {
      try {
        this.clearToken();
        await firstValueFrom(this.client.logout({}));
        this.setAuthState(AuthState.LoggedOut);
        return true;
      } catch (error) {
        this.clearToken();
        this.setAuthState(AuthState.LoggedOut);
        console.log(error);
        return false;
      } finally {
        this._currentSignOutPromise = undefined;
      }
    })();

    return this._currentSignOutPromise;
  }

  // check if the user is authenticated. the endpoint is protected so 401 if not.
  public async user() {
    let expires = new Date();
    expires.setMinutes(expires.getMinutes() + 1);

    //Refresh token if it is expired
    if (!this._token || this._tokenExpires < expires) {
      await firstValueFrom(this.refreshToken());
    }

    return await firstValueFrom(this.manageClient.infoGet());
  }

  public async init() {
    try {
      try {
        await firstValueFrom(this.refreshToken());

        let userInfo = await this.user();
        if (userInfo && userInfo.Email) {
          this.setAuthState(AuthState.LoggedIn);
        }
      } catch (error) {
        console.log(error);
        this.clearToken();
        this.setAuthState(AuthState.LoggedOut);
      }
    } catch (error) {
      console.log(error);
    }
  }

  public async reinit() {
    this.setAuthState(AuthState.Init);
    await this.init();
  }
}
